import { getTenant } from './api';
import { SyncFormat } from './types/integrations/sync';

export * from './constants/visionDataOcr';

export const envs = {
  STAGING: 'STAGING',
  TEST: 'TEST',
  DEMO: 'DEMO',
  PROD: 'PROD',
  DEV: 'DEV'
};

export enum IndesignRoute {
  liner_ad_image = 'liner_ad_image',
  display_ad_image = 'display_ad_image',
  display = 'display',
  convert = 'convert',
  template_specs = 'template_specs',
  display_ad_marginalia = 'display_ad_marginalia',
  paginate = 'paginate',
  paginate_rtf = 'paginate_rtf'
}

export const Collections = {
  subscriptions: 'subscriptions',
  publicNotices: 'publicNotices',
  userNotices: 'usernotices',
  userDrafts: 'userdrafts',
  users: 'users',
  organizations: 'organizations',
  invoices: 'invoices',
  /** Used as a subcollection on invoices * */
  invoiceTransactions: 'invoiceTransactions',
  notifications: 'notifications',
  invites: 'invites',
  adTemplates: 'adTemplates',
  rates: 'rates',
  transfers: 'transfers',
  previewNotices: 'previewNotices',
  emailConfirmations: 'emailConfirmations',
  notes: 'notes',
  media: 'media',
  affidavitTemplates: 'affidavitTemplates',

  cards: 'cards',
  cardInvoices: 'cardInvoices',
  cardTransactions: 'cardTransactions',
  classifieds: 'classifieds',
  noticeFiles: 'noticeFiles',
  migrations: 'migrations',
  events: 'events',
  customers: 'customers',
  customerOrganizations: 'customerOrganizations',
  dwollaEvents: 'dwollaEvents',
  invoiceItems: 'invoiceItems',
  payouts: 'payouts',
  displaySites: 'displaySites',
  notarizations: 'notarizations',
  deadlines: 'deadlines',
  newspaperOrders: 'newspaperOrders',
  modularSizes: 'modularSizes',
  automatedAffidavitSigners: 'automatedAffidavitSigners',
  stripeevents: 'stripeevents',
  briefBatches: 'briefBatches',

  /** This is only used as a subcollection of usernotices */
  courthouse: 'courthouse',

  /**
   * This is only used as a subcollection of users / organizations
   * @deprecated after COREDEV-684 this collection is unused.
   */
  sources: 'sources',

  /** This is only used as a subcollection of usernotices */
  mail: 'mail',

  /** This is not a Firestore collection */
  affidavits: 'affidavits',

  /** This is only used as a subcollection of display sites */
  uploadIDs: 'uploadIDs',

  joinRequests: 'joinRequests',

  /** This is used to store metadata about files uploaded to Column through FTP */
  ftpFiles: 'ftpFiles',

  /** This is only used as a subcollection of organization */
  caches: 'caches',

  /** This is only used as a subcollection of caches */
  cacheEntries: 'cacheEntries',
  publicationIssues: 'publicationIssues',
  publicationIssueAttachments: 'publicationIssueAttachments',
  publicationIssueSections: 'publicationIssueSections',

  updateSettingRequests: 'updateSettingRequests',
  runs: 'runs',
  eeditions: 'eeditions',
  obituaries: 'obituaries',
  orders: 'orders',
  filingTypes: 'filingTypes',
  layouts: 'layouts',
  publishingSettings: 'publishingSettings',
  productPublishingSettings: 'productPublishingSettings',
  ledger: 'ledger',
  adjudicationAreas: 'adjudicationAreas',
  productSiteSettings: 'productSiteSettings',
  orderDetails: 'orderDetails',
  coupons: 'coupons',
  paperChecks: 'paperChecks',
  externalAds: 'externalAds',
  expressEmailConversations: 'expressEmailConversations',
  packages: 'packages',
  publisherPaginationSettingss: 'publisherPaginationSettingss',
  integrationSettings: 'integrationSettings',
  syncAttempts: 'syncAttempts',
  /** Used as a subcollection on newspaperOrders */
  newspaperOrderTransactions: 'newspaperOrderTransactions',
  apiClients: 'apiClients'
  // CODEGEN: COLLECTIONS_CONST - DO NOT DELETE OR MOVE
} as const;

export type CollectionName = (typeof Collections)[keyof typeof Collections];

export const Functions = {
  createCustomer: {
    name: 'createCustomer',
    timeout: 300
  },
  createPublisherDocsOnCall: {
    name: 'createPublisherDocsOnCall',
    timeout: 540
  },
  cloudConvertOnCall: { name: 'cloudConvertOnCall', timeout: 540 },
  donate: { name: 'donate', timeout: 540 },
  createBillingSession: { name: 'createBillingSession', timeout: 540 }
};

/** Zero width space (unicode) */
export const ZWS = '\u200b';

export const EChars = {
  tab: `__enotice-tab-char`
};

let firebaseProject: string;
try {
  firebaseProject = require('../src/config.firebase').default.projectId;
} catch (err) {
  try {
    // For admin
    firebaseProject = require('../../src/config.firebase').default.projectId;
  } catch {
    firebaseProject = process.env.GCLOUD_PROJECT!;
  }
}

export const CLOUDINARY_BUCKET = 'dgqq2xsfd';

export const FIREBASE_PROJECT = firebaseProject;
export const PROJECT_LOCATION = 'us-central1';

// Note: FUNCTIONS_EMULATOR is only set when running the local functions
// development environment (firebase serve or emulators:start)
export const IS_LOCAL_DEV =
  (process.env.FUNCTIONS_EMULATOR === 'true' ||
    process.env.NODE_ENV === 'development') &&
  FIREBASE_PROJECT !== 'enotice-production';

export const IS_TESTING_PROJECT =
  FIREBASE_PROJECT === 'enotice-testing' ||
  FIREBASE_PROJECT === 'enotice-testing2' ||
  FIREBASE_PROJECT === 'enotice-testing3';

const PROD_CLOUD_RUN_API_URL = 'https://api.column.us/v1/';

const getCloudRunApiUrl = (tenant: string, activeOrgId = 'default'): string => {
  switch (FIREBASE_PROJECT) {
    case PRODUCTION_PROJECT:
      return `${PROD_CLOUD_RUN_API_URL}${tenant}/${activeOrgId}/`;
    case DEMO_PROJECT:
      return `https://api.enotice.io/v1/${tenant}/${activeOrgId}/`;
    case DEV_PROJECTS.HATEM:
      return `https://api-62378710186.us-central1.run.app/v1/${tenant}/${activeOrgId}/`;
    case CI_PROJECT:
      return `https://api-123.us-central1.run.app/v1/${tenant}/${activeOrgId}/`;
    case DEV_PROJECTS.BO:
    case DEV_PROJECTS.PAUL:
    case DEV_PROJECTS.KEVIN:
    case DEV_PROJECTS.JUSTIN:
    case DEV_PROJECTS.YAZ:
    case DEV_PROJECTS.JOSH:
    case DEV_PROJECTS.COLIN:
      return `http://localhost:5001/${FIREBASE_PROJECT}/us-central1/api/`;
    default:
      throw new Error(
        `Cloud Run API URL not found for project: ${FIREBASE_PROJECT}`
      );
  }
};

export const getBackendUrl = (
  useCloudRunApi: boolean,
  tenant: string,
  activeOrgId = 'default'
) => {
  if (IS_LOCAL_DEV) {
    return useCloudRunApi
      ? getCloudRunApiUrl(tenant, activeOrgId)
      : `http://localhost:5001/${FIREBASE_PROJECT}/us-central1/api/`;
  }

  return useCloudRunApi
    ? getCloudRunApiUrl(tenant, activeOrgId)
    : `https://us-central1-${FIREBASE_PROJECT}.cloudfunctions.net/api/`;
};

export const PLAID_PUBLIC_KEY = '541c7f3939c359167f48904d2571a0';

export const DEFAULT_ACCEPT =
  '.docx,.doc,.csv,.xls,.xlsx,.txt,.rtf,.jpg,.jpeg,.png,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/rtf,text/rtf,image/jpeg,image/png';

export const PRODUCTION_PROJECT = 'enotice-production';
export const STAGING_PROJECT = 'column-staging';
export const DEMO_PROJECT = 'enotice-demo-8d99a';
export const SAM_PROJECT = 'column-sam';
export const DEV_PROJECTS = {
  PAUL: 'column-paul',
  BO: 'column-bo',
  KEVIN: 'kevinville',
  JUSTIN: 'column-justin',
  YAZ: 'column-yaz',
  JOSH: 'column-josh',
  HATEM: 'enotice-example-f061a',
  COLIN: 'column-colin'
};

export const CI_PROJECT = 'not-a-project';
export const isProdEnv = FIREBASE_PROJECT === PRODUCTION_PROJECT;

// We only configure the VPC connector for this function on prod, so this URL should always point there.
export const getIntegrationProxyBackendUrl = (
  useCloudRunIntegrationProxy: boolean
) => {
  // TODO: add custom domain url
  if (useCloudRunIntegrationProxy) {
    return 'https://integrationsproxy-753063764402.us-central1.run.app/v1/proxy';
  }

  return `https://us-central1-${PRODUCTION_PROJECT}.cloudfunctions.net/integrationProxy/`;
};

/**
 * Currently the only gateway we specify an account code for is Payway, which is
 * currently coupled to the SyncFormat (xmlExport.format) specified on the organization.
 */
export type GatewayAccountCode = SyncFormat;

// DEFAULT_TEMPLATE_ID_V2 is now the default template with customized headers support
export const DEFAULT_TEMPLATE_ID_V2 = 'default-20260413';

/** Stripe Account ID */
export const ENOTICE_ACCOUNT_ID_PROD = 'acct_1F4vICCGonLIgiaZ';
export const ENOTICE_ACCOUNT_ID_DEV = 'acct_1FzOn6EwSpcYm9q2';

/** Default convenience fee values */
export const ENOTICE_CONVENIENCE_FEE = 10;
export const ENOTICE_CONVENIENCE_FEE_GOVT = 0;

export const COLUMN_EXPRESS_EMAIL = 'express@column.us';

/**
 * Google Sign In client ID for admin.column.us
 * See: https://console.cloud.google.com/apis/credentials?referrer=search&project=enotice-production
 *
 * TODO: Right now this can only be prod or demo. If we expand to additional projects this will need to be updated.
 */
export const PRODUCTION_COLUMN_ADMIN_CLIENT_ID =
  '753063764402-f49s3kqhic772c9da1ktn03o884dp2l3.apps.googleusercontent.com';

export const DEMO_COLUMN_ADMIN_CLIENT_ID =
  '981763803674-gemrdp2stlo763r85cv2pljv5bl3vk4q.apps.googleusercontent.com';

export const getColumnAdminClientId = (firebaseProjectId: string) => {
  if (firebaseProjectId === PRODUCTION_PROJECT) {
    return PRODUCTION_COLUMN_ADMIN_CLIENT_ID;
  }

  return DEMO_COLUMN_ADMIN_CLIENT_ID;
};

export const DEV_INDESIGN_URL = 'https://indesign-dev.enotice.io';

/**
 * This is used in an additional fee's description to identify the fee as a Column Rep Fee
 *
 * TODO: This will eventually be replaced as we work towards a more specific fee structure
 * and better ways to identify the fee type
 */
export const COLUMN_REP_FEE = 'Column Rep Fee';

export const LogosInLiners = {
  /** Allow uploaded images of up to 2MB */
  MAX_UPLOAD_SIZE_BYTES: 2 * 1024 * 1024
};

export const COLUMN_LOGO =
  'https://firebasestorage.googleapis.com/v0/b/enotice-demo-8d99a.appspot.com/o/column-landing%2FLOGO_BLUE_1_BLUE_2.png?alt=media&token=4ef89835-3a6c-4df3-be53-570081b8e4c8';

export const getIndesignServiceUrl = (useCloudRunApi: boolean) => {
  if (FIREBASE_PROJECT === 'enotice-production') {
    return `${getBackendUrl(useCloudRunApi, getTenant())}indesign`;
  }

  return useCloudRunApi
    ? `https://api.enotice.io/v1/${getTenant()}/default/indesign`
    : 'https://us-central1-enotice-demo-8d99a.cloudfunctions.net/api/indesign';
};

export const INDESIGN_OUTPUT_BUCKET =
  (FIREBASE_PROJECT === 'enotice-production' &&
    'prod-indesign-service-output-bucket') ||
  'demo-indesign-service-output-bucket';

export const COLUMN_SUPPORT_EMAIL = 'help@column.us';

/**
 * TODO: Replace all instances of this string in the codebase with the
 * constant
 */
export const COLUMN_DATE_FORMAT = 'YYYY-MM-DD';
